import { useMemo } from 'react';
import { VenueAddress } from 'utils/shared/coachBuilder';
import Button from 'components/Button';
import VenueLocationsMap from 'components/VenueLocationsMap';
import type { MapVenue } from 'components/VenueLocationsMap';

interface Props {
  venues: VenueAddress[];
  centerLatitude: number;
  centerLongitude: number;
  handleBookHere: (venue: VenueAddress) => void;
}

/**
 * Adapter over the shared venue map. Everything visual lives in
 * `components/VenueLocationsMap`; this only supplies the coach's venue shape and
 * the "Book Here" action.
 */
export default function CoachMap({
  venues,
  centerLatitude,
  centerLongitude,
  handleBookHere,
}: Props) {
  const mapVenues: MapVenue[] = useMemo(
    () =>
      venues.map((venue) => ({
        id: venue.id,
        title: venue.title || venue.address,
        address: venue.address,
        latitude: venue.latitude || centerLatitude,
        longitude: venue.longitude || centerLongitude,
        imageUrl: venue.images?.[0],
        isIndoor: !!venue.indoorCourtCount,
      })),
    [venues, centerLatitude, centerLongitude],
  );

  // The booking handler still wants the original row, so map back by id.
  const venuesById = useMemo(() => new Map(venues.map((venue) => [venue.id, venue])), [venues]);

  return (
    <VenueLocationsMap
      venues={mapVenues}
      distanceFrom={{ latitude: centerLatitude, longitude: centerLongitude }}
      center={{ lat: centerLatitude, lng: centerLongitude }}
      zoom={12}
      options={{ streetViewControl: false, mapTypeControl: false }}
      renderAction={(venue, surface) => {
        const original = venuesById.get(venue.id);
        if (!original) return null;

        return surface === 'sheet' ? (
          <Button variant="primary" sizeMobile="lg" onClick={() => handleBookHere(original)}>
            Book Here
          </Button>
        ) : (
          <Button
            size="sm"
            className="mt-1"
            variant="inverted"
            onClick={() => handleBookHere(original)}
          >
            Book Here
          </Button>
        );
      }}
    />
  );
}
